import { useState } from "react";
import type { Question, QuizFilter, QuestionTracker } from "@shikaku/shared";

type ReviewTab = "bookmarked" | "wrong" | "forgettingCurve";

interface ReviewViewProps {
  questions: Question[];
  tracker: QuestionTracker;
  primaryColor: string;
  onStartQuiz: (filter: QuizFilter, subunit?: string) => void;
}

const tabConfig: { id: ReviewTab; label: string; filter: QuizFilter }[] = [
  { id: "bookmarked", label: "覚えていない", filter: "bookmarked" },
  { id: "wrong", label: "要復習", filter: "wrongOnly" },
  { id: "forgettingCurve", label: "忘却曲線", filter: "forgettingCurve" },
];

export function ReviewView({
  questions,
  tracker,
  primaryColor,
  onStartQuiz,
}: ReviewViewProps) {
  const [activeTab, setActiveTab] = useState<ReviewTab>("bookmarked");

  const filterIds = (() => {
    switch (activeTab) {
      case "bookmarked": return tracker.bookmarkedIDs;
      case "wrong": return tracker.wrongIDs;
      case "forgettingCurve": return tracker.dueForReviewIDs;
    }
  })();

  const filtered = questions.filter((q) => filterIds.has(q.id));

  const subunitOrder: string[] = [];
  const grouped = new Map<string, Question[]>();
  for (const q of filtered) {
    if (!grouped.has(q.subunit)) {
      subunitOrder.push(q.subunit);
      grouped.set(q.subunit, []);
    }
    grouped.get(q.subunit)!.push(q);
  }

  const quizFilter = tabConfig.find((t) => t.id === activeTab)!.filter;

  const emptyMessages: Record<ReviewTab, string> = {
    bookmarked: "覚えていない問題はまだありません",
    wrong: "間違えた問題はまだありません",
    forgettingCurve: "復習が必要な問題はありません",
  };

  return (
    <div style={{ flex: 1, overflowY: "auto" }}>
      {/* Tabs */}
      <div className="tab-bar">
        {tabConfig.map((tab) => {
          const count = (() => {
            switch (tab.id) {
              case "bookmarked": return tracker.bookmarkedIDs.size;
              case "wrong": return tracker.wrongIDs.size;
              case "forgettingCurve": return tracker.dueForReviewIDs.size;
            }
          })();
          return (
            <button
              key={tab.id}
              className={`tab-item ${activeTab === tab.id ? "active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}（{count}）
            </button>
          );
        })}
      </div>

      {/* Content */}
      {subunitOrder.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 20px", color: "#999", fontSize: 15 }}>
          {emptyMessages[activeTab]}
        </div>
      ) : (
        <>
          {/* Start all */}
          <div style={{ padding: "16px 20px" }}>
            <button className="btn-primary" onClick={() => onStartQuiz(quizFilter)}>
              すべて開始（{filtered.length}問）
            </button>
          </div>

          {/* Grouped list */}
          <div style={{ background: "#fff" }}>
            {subunitOrder.map((subunit, i) => {
              const count = grouped.get(subunit)!.length;
              return (
                <div key={subunit}>
                  <button
                    className="list-row"
                    onClick={() => onStartQuiz(quizFilter, subunit)}
                  >
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: "#333" }}>{subunit}</div>
                      <div style={{ fontSize: 13, color: "#999", marginTop: 2 }}>{count}問</div>
                    </div>
                    <svg width="16" height="16" fill="none" stroke="#ccc" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  {i < subunitOrder.length - 1 && <div style={{ margin: "0 20px", borderTop: "1px solid #f0f0f0" }} />}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
